"use client";

import Link from "next/link";
import { FormEvent, useRef, useState } from "react";
import { authenticationServiceUnavailableError } from "../../../lib/authLoginFailure";
import SessionRevocationReviewBanner from "../../components/SessionRevocationReviewBanner";
import { hasSessionRevocationWarning } from "../../../lib/sessionRevocationWarning";

const loginRequestTimeoutMs = 12_000;

export default function AdminLoginClient() {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const inFlight = useRef(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (inFlight.current) return;
    inFlight.current = true;
    // Browser autofill can populate the form before React receives a change event.
    const credentials = new FormData(event.currentTarget);
    setSubmitting(true);
    setError("");
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), loginRequestTimeoutMs);
    let navigating = false;

    try {
      const response = await fetch("/api/admin/staff/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ identifier: String(credentials.get("identifier") || ""), password: String(credentials.get("password") || "") }),
        signal: controller.signal,
      });
      const result = await response.json();

      if (!response.ok) {
        setError(
          response.status === 429 || response.status === 503
            ? result.error || authenticationServiceUnavailableError
            : "Invalid login credentials.",
        );
        return;
      }

      navigating = true;
      window.location.assign(result.redirectTo === "/admin" && hasSessionRevocationWarning("staff") ? "/admin/profile" : result.redirectTo || "/admin/mfa/verify");
    } catch {
      setError(authenticationServiceUnavailableError);
    } finally {
      clearTimeout(timeout);
      if (!navigating) {
        inFlight.current = false;
        setSubmitting(false);
      }
      setPassword("");
    }
  }

  return (
    <div className="mt-8 rounded-md border border-[var(--vintera-border)] bg-white p-6 shadow-sm">
      <SessionRevocationReviewBanner accountKind="staff" />
      <form action="/api/admin/staff/login" method="post" onSubmit={handleSubmit}>
        <label className="block">
          <span className="text-sm font-semibold text-[var(--vintera-navy)]">
            Email or staff username
          </span>
          <input
            name="identifier"
            value={identifier}
            onChange={(event) => setIdentifier(event.target.value)}
            autoComplete="username"
            required
            className="mt-2 w-full rounded-md border border-[var(--vintera-border)] px-4 py-3 outline-none transition focus:border-[var(--vintera-navy)]"
          />
        </label>

        <label className="mt-5 block">
          <span className="text-sm font-semibold text-[var(--vintera-navy)]">
            Password
          </span>
          <input
            name="password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            type="password"
            autoComplete="current-password"
            required
            className="mt-2 w-full rounded-md border border-[var(--vintera-border)] px-4 py-3 outline-none transition focus:border-[var(--vintera-navy)]"
          />
        </label>

        {error && (
          <p className="mt-5 rounded-md bg-red-100 p-4 text-sm font-medium text-red-800">
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={submitting}
          className="mt-6 w-full rounded-md bg-[var(--vintera-navy)] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[var(--vintera-navy-hover)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? "Signing in..." : "Sign In"}
        </button>

        <Link
          href="/admin/forgot-password"
          className="mt-5 inline-flex text-sm font-semibold text-[var(--vintera-navy)] hover:underline"
        >
          Forgot password?
        </Link>
      </form>
    </div>
  );
}
